import { useEffect, useState,useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { Box, useTheme } from '@mui/system';
import { Typography } from '@mui/material';

import { PKDCheckbox } from 'components/Consent/Checkbox';
import { SubHeading } from 'components/Consent/style';
import { PKDFilledButton } from 'components/FilledButton';
import { PKDModal } from 'components/Modal';
import { PKDAccordion } from 'components/PKDAccordion';
import { PKDOnboardingLayout } from 'components/PKDOnboardingLayout';
import { ReactComponent as PrinterIcon } from 'images/Consent/printer.svg';
import {
  AccentTitle,
  PKDLink as Link,
  PageTitle,
  PrintButtonWrapper,
  Wrapper,
} from './styles';
import { openSnackbar } from 'store/application';
import { TextInput } from 'shared-components/Input';
import { useLogout } from 'hooks/useLogout';
import {
  useUpsertPersonAttributeMutation,
  useUpdateV3PersonMutation,
} from 'store/api/pkd/endpoints/person';
import { assentData, consentData } from './utils';
import { useGetConsentsQuery } from 'store/api/pkd/endpoints/entity';
import {
  getAttributesPayload,
  getPersonAttribute,
  getPersonMedicalRecords,
} from 'pages/MedicalInformation/utils';
import { isBelowAge } from 'utils/date';
import { ErrorWrapper } from 'shared-components/ErrorWrapper';
import { PersonStatus, StudyRoleId } from 'utils/enum';
import SkeletonLoader from './skeletonLoader';
import { useGetPersonAndAttributes } from 'hooks/useGetPersonAndAttributes';

import { useStudyArm } from 'hooks/useStudyArm';
import { getAttribute } from 'utils/person';

export const Consent = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { handleLogout } = useLogout();
  const {
    isPersonLoading,
    personId,
    person,
    isPersonAttributesFetching,
    isPersonFetching,
  } = useGetPersonAndAttributes();
  const { isCreatePersonStudyArmAttributesLoading, handleUpdatePersonStudyArmTable, handleCreatePersonStudyArmAttribute } = useStudyArm(personId);

  const [upsertPersonAttribute, { isLoading: isPersonAttributeUpserting }] =
    useUpsertPersonAttributeMutation();
  const [updateV3Person, { isLoading: isPersonUpdating }] =
    useUpdateV3PersonMutation();
  

  // States & Refs
  const hasSavedConsent = useRef(false);
  const participantName = `${getAttribute(person, 'first_name')} ${getAttribute(person, 'last_name')}`;
  const medicalInfo = getPersonMedicalRecords(person?.attributes, {
    date_of_birth : '',
  });
  let userIsBelow18 =
    medicalInfo?.date_of_birth && isBelowAge(medicalInfo?.date_of_birth, 18);

  const { data: consents, isLoading: isConsentLoading } = useGetConsentsQuery(
    {
      entityName   : 'consents',
      entityParams : {
        personId,
        studyRoleId : userIsBelow18
          ? StudyRoleId.Caregiver
          : StudyRoleId.Participant,
      },
    },
    { skip: isPersonLoading }
  );

  const participantConsent =
    consents && consents.length > 4
      ? consents.slice(0, consents.length - 1)
      : consents;

  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
  } = useForm({
    mode          : 'onChange',
    defaultValues : {
      consent1 : false,
      consent2 : false,
      consent3 : false,
      consent4 : false,
      ...(consents?.length === 5 ? { consent5: false, careGiverName: '' } : {}),
    },
  });

  const [isDeclineModalOpen, setIsDeclineModalOpen] = useState(false);
  const [isAssentModalOpen, setIsAssentModalOpen] = useState(false);
  const [allConsentViewed, setAllConsentViewed] = useState(false);
  const [, setAllAssentViewed] = useState(false);

  const toggleDeclineModal = () => setIsDeclineModalOpen((prev) => !prev);
  const toggleAssentModal = () => setIsAssentModalOpen((prev) => !prev);

  const handlePersonConsent = async (payloadData, personData) => {
    const attributes = getPersonAttribute(person?.attributes, payloadData);
    const body = getAttributesPayload(payloadData, attributes);
    if (body.length > 0) {
      const { error: upsertPersonError } = await upsertPersonAttribute({
        body,
        personId,
      });
      let errorCount = upsertPersonError ? 1 : 0;
      // update v3 person
      const { error: updatePersonError } = await updateV3Person({
        personId,
        attributes : personData,
      });
      errorCount = updatePersonError ? errorCount++ : errorCount;
      if (errorCount > 0) {
        return dispatch(
          openSnackbar({
            type : 'error',
            message :
              'Could not save your consent information. Please try again',
          })
        );
      }
    }
    hasSavedConsent.current = true;
    dispatch(
      openSnackbar({
        type    : 'success',
        message : 'Consent information successfully saved',
      })
    );
  };

  const handleDeclineConsent = async () => {
    const payloadData = {
      consentDate : new Date(),
    };
    const personData = {
      status       : PersonStatus.Withdrawn,
      statusReason : 'Participant declined conset',
    };
    await handlePersonConsent(payloadData, personData);
    handleLogout();
  };

  const onSubmit = handleSubmit(async (data) => {
    const canSubmit =
      Object.values(data).every((value) => Boolean(value)) && allConsentViewed;
    if (!canSubmit) {
      return dispatch(
        openSnackbar({
          type : 'error',
          message :
            'Please make sure you read all the consent documents before accepting',
        })
      );
    }

    // create person attribute
    const payloadData = {
      ...(data?.careGiverName
        ? { pkdStudyConsentParentName: data?.careGiverName }
        : {}),
      consentDate : new Date(),
    };
    const personData = {
      status       : PersonStatus.Enrolled,
      statusReason : 'Participant has been enrolled',
    };
    await handlePersonConsent(payloadData, personData);
    await handleUpdatePersonStudyArmTable(PersonStatus.Enrolled);
    await handleCreatePersonStudyArmAttribute(person);
    
  });

  useEffect(() => {
    const hasCompletedSaving =
      hasSavedConsent.current &&
      !isPersonFetching &&
      !isPersonAttributesFetching &&
      !isPersonUpdating &&
      !isPersonFetching;
    if (hasCompletedSaving) {
      return navigate('/homepage', { replace: true });
    }
  }, [
    hasSavedConsent.current,
    isPersonUpdating,
    isPersonFetching,
    isPersonAttributesFetching,
  ]);

  const validateName = (userName) => {
    const userNameSort = userName
      .replace(/ /g, '')
      .split('')
      .sort((a, b) => a.localeCompare(b))
      .join('');
    const participantNameSort = participantName
      .replace(/ /g, '')
      .split('')
      .sort((a, b) => a.localeCompare(b))
      .join('');
    return userNameSort === participantNameSort;
  };

  const loading = isPersonLoading || isConsentLoading;

  return (
    <PKDOnboardingLayout>
      <Wrapper>
        <PrintButtonWrapper
          text='Print'
          color={theme.palette.primary.gray6}
          borderColor={theme.palette.primary.gray3}
          startIcon={<PrinterIcon />}
          width='98px'
          onClickHandler={() => window.print()}
        />
        <Box
          marginTop='40px'>
          <PageTitle
            textAlign='center'>Consent</PageTitle>
          <Box
            marginTop='34px'>
            <PKDAccordion
              data={consentData}
              setAllViewed={setAllConsentViewed}
              hasDangerousElements={true}
            />
          </Box>
          {loading
            ? (
              <SkeletonLoader
                isMinor={consents?.length > 4}
              />
            )
            : (
              <form
                onSubmit={handleSubmit(onSubmit)}>
                <Box
                  marginTop='38px'>
                  {participantConsent?.map((consent, index) => (
                    <Box
                      key={consent.id} marginBottom='38px'>
                      <Controller
                        control={control}
                        name={`consent${index + 1}`}
                        rules={{
                          required : {
                            value   : true,
                            message : 'This field is required',
                          },
                        }}
                        render={({ field: { onChange } }) => (
                          <ErrorWrapper
                            message={errors[`consent${index + 1}`]}>
                            <PKDCheckbox
                              value={watch(`consent${index + 1}`)}
                              onChange={onChange}
                              label={consent.content}
                              error={errors[`consent${index + 1}`]}
                              hasErrors={
                                errors[`consent${index + 1}`] && {
                                  type    : 'custom',
                                  message : errors[`consent${index + 1}`]?.message,
                                }
                              }
                            />
                          </ErrorWrapper>
                        )}
                      />
                    </Box>
                  ))}
                  {consents?.length > 4 && (
                    <>
                      <Box>
                        <SubHeading>
                        If you are registering a child under 18 years of age:
                        </SubHeading>
                        <Box
                          marginBottom='38px' marginTop='10px'>
                          <Controller
                            control={control}
                            name={'consent5'}
                            rules={{
                              required : {
                                value   : true,
                                message : 'This field is required',
                              },
                            }}
                            render={({ field: { onChange } }) => (
                              <ErrorWrapper
                                message={errors.consent5}>
                                <PKDCheckbox
                                  value={watch('consent5')}
                                  onChange={(e) => {
                                    onChange(e);
                                    toggleAssentModal();
                                  }}
                                  label={consents[consents.length - 1]?.content}
                                  error={errors.consent5}
                                  hasErrors={
                                    errors.consent5 && {
                                      type    : 'custom',
                                      message : errors.consent5?.message,
                                    }
                                  }
                                />
                              </ErrorWrapper>
                            )}
                          />
                        </Box>
                      </Box>
                      <Box
                        marginTop='25px' marginBottom='46px' width='292px'>
                        <Controller
                          control={control}
                          name='careGiverName'
                          rules={{
                            required : {
                              value   : true,
                              message : 'Enter your name',
                            },
                            validate : (value) => validateName(value),
                          }}
                          render={({ field: { onChange } }) => (
                            <ErrorWrapper
                              message={errors.careGiverName}>
                              <TextInput
                                placeholder='Name of Parent/Legal Guardian'
                                color={theme.palette.primary.blue1}
                                fullWidth
                                value={watch('careGiverName')}
                                onChange={onChange}
                                error={errors.careGiverName}
                                hasErrors={
                                  errors.careGiverName && {
                                    type    : 'custom',
                                    message : 'You must enter your full name',
                                  }
                                }
                              />
                            </ErrorWrapper>
                          )}
                        />
                      </Box>
                    </>
                  )}
                  <Box
                    marginBottom='38px'
                    marginTop='10px'
                    display='flex'
                    justifyContent='center'>
                    <PKDFilledButton
                      sx={{
                        marginRight : '8px',
                      }}
                      onClick={onSubmit}
                      isLoading={
                        (isPersonUpdating ||
                        isPersonAttributeUpserting ||
                        isPersonFetching ||
                        isPersonAttributesFetching ||
                        isCreatePersonStudyArmAttributesLoading) &&
                      !isDeclineModalOpen
                      }
                      disabled={
                        isPersonUpdating ||
                      isPersonAttributeUpserting ||
                      isPersonFetching ||
                      isPersonAttributesFetching ||
                      isCreatePersonStudyArmAttributesLoading
                      }>
                    Accept
                    </PKDFilledButton>
                    <PKDFilledButton
                      sx={{
                        backgroundColor : theme.palette.primary.grey3,
                        color           : theme.palette.primary.gray7,
                        marginLeft      : '8px',
                      }}
                      onClick={toggleDeclineModal}>
                    Decline
                    </PKDFilledButton>
                  </Box>
                </Box>
              </form>
            )}
        </Box>
        {/* decline modal */}
        <PKDModal
          isOpen={isDeclineModalOpen}
          onClose={toggleDeclineModal}
          title='Decline consent'>
          <Box
            padding='40px 16px'>
            <Typography
              fontSize='16px' lineHeight='24px' textAlign='center'>
              Are you sure you want to decline the Informed Consent?
            </Typography>
          </Box>
          <Box
            margin='24px auto' display='flex' justifyContent='center'>
            <PKDFilledButton
              sx={{
                backgroundColor : theme.palette.primary.grey3,
                color           : theme.palette.primary.gray7,
                marginRight     : '8px',
                width           : '180px',
              }}
              onClick={handleDeclineConsent}
              isLoading={
                (isPersonUpdating || isPersonAttributeUpserting) &&
                isDeclineModalOpen
              }
              disabled={
                (isPersonUpdating || isPersonAttributeUpserting) &&
                isDeclineModalOpen
              }>
              Yes
            </PKDFilledButton>
            <PKDFilledButton
              sx={{
                backgroundColor : theme.palette.primary.grey3,
                color           : theme.palette.primary.gray7,
                marginLeft      : '8px',
                width           : '180px',
              }}
              onClick={toggleDeclineModal}>
              No
            </PKDFilledButton>
          </Box>
        </PKDModal>

        {/* child under 18 modal */}
        <PKDModal
          isOpen={isAssentModalOpen}
          onClose={toggleAssentModal}
          title='Assent'
          width='926px'>
          <Box
            margin='40px 100px'>
            <Box>
              <PrintButtonWrapper
                text='Print'
                color={theme.palette.primary.gray6}
                borderColor={theme.palette.primary.gray3}
                startIcon={<PrinterIcon />}
                width='98px'
              />
              <Box
                marginBottom='12px'>
                <AccentTitle
                  textAlign='center'
                  fontSize='14px'
                  lineHeight='24px'>
                  ADPKD REGISTRY ASSENT FORM
                  <br />
                  (FOR AGES 12 TO 17 TO PARTICIPATE)
                </AccentTitle>
              </Box>
              <Box
                marginBottom='12px'>
                <AccentTitle>
                  The PKD Foundation
                  <br />
                  1001 E 101st Terrace, Suite 220
                  <br />
                  Kansas City, MO 64131
                </AccentTitle>
              </Box>
              <Box
                display='flex'
                justifyContent='space-between'
                marginBottom='30px'>
                <Box>
                  <AccentTitle>816-264-8478</AccentTitle>
                  <AccentTitle>800-753-4673 (24 hours)</AccentTitle>
                  <AccentTitle>
                    1.800.PKD.CURE (753.2873)
                    <br />
                    <Link
                      href='mailto:registry@pkdcure.org'>
                      registry@pkdcure.org
                    </Link>
                  </AccentTitle>
                </Box>
              </Box>
            </Box>
            <Box
              mb={1}>
              <AccentTitle>
                “You” refers to “You” or “Your Child” throughout this document.
              </AccentTitle>
            </Box>
            <PKDAccordion
              data={assentData}
              hasDangerousElements={true}
              setAllViewed={setAllAssentViewed}
            />
            <Box
              margin='24px auto' display='flex' justifyContent='center'>
              <PKDFilledButton
                sx={{
                  backgroundColor : theme.palette.primary.grey3,
                  color           : theme.palette.primary.gray7,
                  marginRight     : '8px',
                  width           : '180px',
                }}
                onClick={toggleAssentModal}>
                Cancel
              </PKDFilledButton>
            </Box>
          </Box>
        </PKDModal>
      </Wrapper>
    </PKDOnboardingLayout>
  );
};
